// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  MoreVertical,
  Calendar,
  Clock,
  X,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";
import { CareGiverContext } from "../context/CareGiverContext";
import { AppContext } from "../context/AppContext";
import NotOnboardedMessage from "../components/NotOnboardedMessage";
import { caregiverNetAmount } from "../utils/paymentDisplay";

const BookingList = () => {
  const { t } = useTranslation();

  const getTranslatedStatus = (status) => {
    switch (status) {
      case "New":
        return t("bookings.statusNew");
      case "In_Progress":
        return t("bookings.statusInProgress");
      case "Work_Started":
        return t("bookings.statusWorkStarted");
      case "Completed":
        return t("bookings.statusCompleted");
      case "Cancelled":
        return t("bookings.statusCancelled");
      default:
        return status ? status.replace(/_/g, " ") : "";
    }
  };

  const {
    bookings,
    bookingsPagination,
    loading,
    getBookings,
    cancelBooking,
    completeBooking,
    notOnboarded,
    profileData,
  } = useContext(CareGiverContext);
  const { slotDateFormat, currency } = useContext(AppContext);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [showFilters, setShowFilters] = useState(false);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Modal States
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Loading states for modal actions
  const [cancelling, setCancelling] = useState(false);
  const [completing, setCompleting] = useState(false);

  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showStatusDropdown]);

  useEffect(() => {
    // Wait for profile to load before fetching bookings
    if (!profileData) return;
    // Don't fetch if not onboarded
    if (notOnboarded) return;
    getBookings({
      page: bookingsPagination.page,
      limit: bookingsPagination.limit,
      status: statusFilter,
      search: searchTerm,
    });
  }, [
    profileData,
    notOnboarded,
    bookingsPagination.page,
    bookingsPagination.limit,
    statusFilter,
    searchTerm,
  ]);

  // Auto-show filters on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowFilters(true);
      } else {
        setShowFilters(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a dropdown button or inside a dropdown menu
      const isDropdownButton = event.target.closest("[data-dropdown-button]");
      const isDropdownMenu = event.target.closest("[data-dropdown-menu]");

      if (!isDropdownButton && !isDropdownMenu) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle Enter key press for search
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (
        e.key === "Enter" &&
        document.activeElement === searchInputRef.current
      ) {
        handleSearch();
      }
    };

    document.addEventListener("keypress", handleKeyPress);
    return () => document.removeEventListener("keypress", handleKeyPress);
  }, [tempSearchTerm]);

  const handleActionClick = (id, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const openHistory = (bk) => {
    setSelectedBooking(bk);
    setShowHistoryModal(true);
    setActiveDropdown(null);
  };

  const openCancel = (bk) => {
    setSelectedBooking(bk);
    setCancelReason("");
    setCancelError("");
    setCancelling(false);
    setShowCancelModal(true);
    setActiveDropdown(null);
  };

  const openComplete = (bk) => {
    setSelectedBooking(bk);
    setCompleting(false);
    setShowCompleteModal(true);
    setActiveDropdown(null);
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) {
      setCancelError(t("bookings.cancelReasonRequired"));
      return;
    }

    setCancelling(true);
    try {
      await cancelBooking(selectedBooking.id, cancelReason);
      // cancelBooking in context already calls getBookings() on success — no need to call again
      setShowCancelModal(false);
    } catch (error) {
      console.error("Failed to cancel booking:", error);
    } finally {
      setCancelling(false);
    }
  };

  const handleCompleteConfirm = async () => {
    setCompleting(true);
    try {
      await completeBooking(selectedBooking.id);
      // completeBooking in context already calls getBookings() on success — no need to call again
      setShowCompleteModal(false);
    } catch (error) {
      console.error("Failed to complete booking:", error);
    } finally {
      setCompleting(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    const trimmedValue = tempSearchTerm.trim();
    if (trimmedValue !== searchTerm) {
      setSearchTerm(trimmedValue);
      // Reset to page 1 when searching
      getBookings({
        page: 1,
        limit: bookingsPagination.limit,
        status: statusFilter,
        search: trimmedValue,
      });
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setTempSearchTerm("");
    if (searchTerm !== "") {
      setSearchTerm("");
      // Reset to page 1 when clearing search
      getBookings({
        page: 1,
        limit: bookingsPagination.limit,
        status: statusFilter,
        search: "",
      });
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setStatusFilter("All Statuses");
    getBookings({
      page: 1,
      limit: bookingsPagination.limit,
      status: "All Statuses",
      search: "",
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    getBookings({
      page,
      limit: bookingsPagination.limit,
      status: statusFilter,
      search: searchTerm,
    });
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    getBookings({
      page: 1, // Reset to page 1 when changing items per page
      limit: Number(limit),
      status: statusFilter,
      search: searchTerm,
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    getBookings({
      page: 1,
      limit: bookingsPagination.limit,
      status: value,
      search: searchTerm,
    });
  };

  // Show spinner while profile is loading
  if (!profileData) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-dark-muted">
          {t("bookings.loading")}
        </p>
      </div>
    );
  }

  if (notOnboarded) {
    return <NotOnboardedMessage featureName="bookings" />;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            {t("bookings.title")}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm">
            {t("bookings.subtitle")}
          </p>
        </div>

        {/* Filter Toggle Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-fit"
        >
          <Filter size={20} className="text-primary" />
          {showFilters ? t("bookings.hideFilters") : t("bookings.showFilters")}
        </button>
      </div>

      {/* Filter Toolbar */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("bookings.searchPlaceholder")}
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                className="w-full pl-12 pr-20 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium transition-all"
              />

              {/* Search Icon Button (only shows when text is typed) */}
              {tempSearchTerm.trim() && (
                <button
                  onClick={handleSearch}
                  className="absolute right-12 top-1/2 -translate-y-1/2 p-2 text-primary hover:text-primary-dark transition-colors"
                  title="Search"
                >
                  <Search size={18} />
                </button>
              )}

              {/* Clear Button (only shows when text is typed) */}
              {tempSearchTerm.trim() && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-0">
              <div className="relative min-w-[180px] dropdown-container">
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span>
                    {statusFilter === "All Statuses"
                      ? t("bookings.allStatuses")
                      : statusFilter === "New"
                        ? t("bookings.statusNew")
                        : statusFilter === "In_Progress"
                          ? t("bookings.statusInProgress")
                          : statusFilter === "Work_Started"
                            ? t("bookings.statusWorkStarted")
                            : statusFilter === "Completed"
                              ? t("bookings.statusCompleted")
                              : t("bookings.statusCancelled")}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-primary transition-transform ${
                      showStatusDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showStatusDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                    {[
                      {
                        value: "All Statuses",
                        label: t("bookings.allStatuses"),
                      },
                      { value: "New", label: t("bookings.statusNew") },
                      {
                        value: "In_Progress",
                        label: t("bookings.statusInProgress"),
                      },
                      {
                        value: "Work_Started",
                        label: t("bookings.statusWorkStarted"),
                      },
                      {
                        value: "Completed",
                        label: t("bookings.statusCompleted"),
                      },
                      {
                        value: "Cancelled",
                        label: t("bookings.statusCancelled"),
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          handleStatusChange(option.value);
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === option.value
                            ? "bg-primary text-white dark:bg-primary"
                            : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Button */}
              <button
                onClick={resetFilters}
                className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors lg:ml-3 flex items-center justify-center gap-2"
              >
                {t("bookings.resetAll")}
              </button>
            </div>
          </div>

          {/* Search Info Text */}
          <div className="mt-3 text-xs text-gray-500 dark:text-dark-muted">
            <p>{t("bookings.searchHint")}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-dark-muted">
            {t("bookings.loading")}
          </p>
        </div>
      )}

      {!loading && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          {/* Responsive wrapper for table */}
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <div className="min-w-[768px] md:min-w-full">
              <div className="overflow-visible min-h-[400px]">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("bookings.clientDetails")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("bookings.date")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("payments.amount")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("bookings.status")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("bookings.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {bookings && bookings.length > 0 ? (
                      bookings.map((bk) => {
                        const isCompleted = bk.status === "Completed";
                        const isWorkStarted = bk.status === "Work_Started";
                        const isCancelled = bk.status === "Cancelled";
                        const isInProgress = bk.status === "In_Progress";

                        // Display label with proper formatting
                        const displayStatus = bk.status.replace(/_/g, " ");

                        const statusClass = isCompleted
                          ? "bg-gray-600"
                          : isWorkStarted
                            ? "bg-primary"
                            : isCancelled
                              ? "bg-red-600"
                              : isInProgress
                                ? "bg-blue-600"
                                : "bg-yellow-500";
                        return (
                          <tr
                            key={bk.id}
                            className="group hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-4 md:px-6 py-4">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 p-0.5 shrink-0">
                                  <img
                                    src={
                                      bk.user?.image || "/default-avatar.png"
                                    }
                                    alt={bk.user?.name || "Client"}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-semibold text-text dark:text-dark-text text-sm truncate">
                                    {bk.user?.name || "N/A"}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-muted mt-0.5">
                                    {bk.serviceCategory?.label && (
                                      <span className="truncate">
                                        {bk.serviceCategory.label}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-dark-text font-medium">
                                  <Calendar
                                    size={14}
                                    className="text-gray-400 shrink-0"
                                  />
                                  <span className="truncate">
                                    {slotDateFormat(bk.slotDate)}
                                  </span>
                                </div>
                                {bk.slotTime && (
                                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-muted">
                                    <Clock size={14} className="shrink-0" />
                                    <span className="truncate">
                                      {bk.slotTime}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="text-sm font-bold text-text dark:text-dark-text whitespace-nowrap">
                                {currency}
                                {bk.contract?.monthlySalary
                                  ? caregiverNetAmount(
                                      bk.contract.monthlySalary,
                                    ).toFixed(2)
                                  : bk.agreedAmount
                                    ? caregiverNetAmount(bk.agreedAmount).toFixed(2)
                                    : "0.00"}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-dark-muted">
                                {bk.contract
                                  ? t("bookings.monthlySalary")
                                  : t("bookings.agreedAmount")}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap ${statusClass}`}
                              >
                                <span className="truncate">
                                  {getTranslatedStatus(bk.status)}
                                </span>
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 text-center relative">
                              <button
                                data-dropdown-button
                                onClick={(e) => handleActionClick(bk.id, e)}
                                className="p-2 text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all mx-auto"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {/* Dropdown Menu */}
                              {activeDropdown === bk.id && (
                                <div
                                  data-dropdown-menu
                                  className="absolute right-4 md:right-8 top-full md:top-8 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right md:origin-top-left"
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => openHistory(bk)}
                                      className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                                    >
                                      <Clock size={16} />{" "}
                                      {t("bookings.viewHistory")}
                                    </button>

                                    {/* Show Complete for Work Started bookings */}
                                    {bk.status === "Work_Started" && (
                                      <button
                                        onClick={() => openComplete(bk)}
                                        className="w-full text-left px-4 py-2.5 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2 border-t border-gray-100 dark:border-dark-border"
                                      >
                                        <Clock size={16} />{" "}
                                        {t("bookings.markComplete")}
                                      </button>
                                    )}

                                    {/* Show Cancel only for New or In_Progress */}
                                    {["New", "In_Progress"].includes(
                                      bk.status,
                                    ) && (
                                      <button
                                        onClick={() => openCancel(bk)}
                                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 border-t border-gray-100 dark:border-dark-border"
                                      >
                                        <X size={16} />{" "}
                                        {t("bookings.cancelBooking")}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 md:px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                        >
                          {searchTerm || statusFilter !== "All Statuses"
                            ? t("bookings.noBookings")
                            : t("bookings.noBookings")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination Component */}
          {bookingsPagination && (
            <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
              <Pagination
                currentPage={bookingsPagination.page}
                totalPages={bookingsPagination.totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={bookingsPagination.limit}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-4 md:px-6 py-4 md:py-5 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <h2 className="text-lg font-bold text-text dark:text-dark-text">
                {t("bookings.title")}
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-text dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                <div>
                  <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                    {t("bookings.client")}
                  </p>
                  <p className="font-semibold text-text dark:text-dark-text text-lg">
                    {selectedBooking.user?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                    {t("bookings.date")}
                  </p>
                  <p className="font-semibold text-text dark:text-dark-text text-lg">
                    {slotDateFormat(selectedBooking.slotDate)}
                    {selectedBooking.slotTime &&
                      ` – ${selectedBooking.slotTime}`}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                    {t("bookings.currentStatus")}
                  </p>
                  <p className="font-semibold text-text dark:text-dark-text text-lg">
                    {getTranslatedStatus(selectedBooking.status)}
                  </p>
                </div>
              </div>

              {/* History Table */}
              {selectedBooking.statusHistory?.length > 0 ? (
                <>
                  <h3 className="text-gray-500 dark:text-dark-muted text-sm mb-3">
                    {t("bookings.statusHistory")}
                  </h3>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 dark:text-dark-text">
                              {t("bookings.status")}
                            </th>
                            <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 dark:text-dark-text">
                              {t("bookings.changedBy")}
                            </th>
                            <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 dark:text-dark-text">
                              {t("bookings.changedAt")}
                            </th>
                            <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 dark:text-dark-text">
                              {t("bookings.reminder")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {selectedBooking.statusHistory.map((item, i) => (
                            <tr
                              key={i}
                              className="bg-white dark:bg-dark-surface"
                            >
                              <td className="px-3 md:px-4 py-2 md:py-3 text-gray-600 dark:text-dark-muted">
                                {getTranslatedStatus(item.status)}
                              </td>
                              <td className="px-3 md:px-4 py-2 md:py-3 text-gray-600 dark:text-dark-muted">
                                {item.changedBy?.name || "-"}
                              </td>
                              <td className="px-3 md:px-4 py-2 md:py-3 text-gray-600 dark:text-dark-muted whitespace-nowrap">
                                {new Date(item.changedAt).toLocaleString()}
                              </td>
                              <td className="px-3 md:px-4 py-2 md:py-3 text-gray-600 dark:text-dark-muted">
                                {item.reminder || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
                  {t("bookings.noHistory")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 md:p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
                <AlertCircle size={24} />
              </div>
              <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
                {t("bookings.cancel")}
              </h3>
              <p className="text-gray-500 dark:text-dark-muted mb-4 md:mb-6">
                {t("bookings.cancelConfirmText", {
                  name: selectedBooking.user?.name || "this client",
                })}
              </p>
              <div className="mt-4 mb-4 md:mb-6">
                <textarea
                  placeholder={t("bookings.cancelReasonPlaceholder")}
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(e.target.value);
                    if (e.target.value.trim()) setCancelError("");
                  }}
                  disabled={cancelling}
                  className={`w-full h-24 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border text-text dark:text-dark-text text-sm resize-none focus:ring-2 outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                    cancelError
                      ? "border-red-500 focus:ring-red-500/20"
                      : "border-gray-200 dark:border-gray-700 focus:ring-red-500/20"
                  }`}
                ></textarea>
                {cancelError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {cancelError}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => !cancelling && setShowCancelModal(false)}
                  disabled={cancelling}
                  className={`flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-850 text-text dark:text-dark-text font-medium transition-colors ${
                    cancelling
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {cancelling
                    ? t("bookings.cancelling")
                    : t("bookings.keepBooking")}
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={cancelling}
                  className={`flex-1 px-4 py-2 rounded-xl bg-red-600 text-white font-medium transition-colors shadow-lg shadow-red-200 dark:shadow-none ${
                    cancelling
                      ? "opacity-50 cursor-not-allowed hover:bg-red-600"
                      : "hover:bg-red-700"
                  }`}
                >
                  {cancelling ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      {t("bookings.cancelling")}
                    </>
                  ) : (
                    t("bookings.confirmCancel")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Confirmation Modal */}
      {showCompleteModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 md:p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4 text-green-600 dark:text-green-400">
                <AlertCircle size={24} />
              </div>
              <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
                {t("bookings.complete")}
              </h3>
              <p className="text-gray-500 dark:text-dark-muted mb-4 md:mb-6">
                {t("bookings.completeConfirmText", {
                  name: selectedBooking.user?.name || "this client",
                })}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => !completing && setShowCompleteModal(false)}
                  disabled={completing}
                  className={`flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-850 text-text dark:text-dark-text font-medium transition-colors ${
                    completing
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {completing
                    ? t("bookings.completing")
                    : t("bookings.noCancel")}
                </button>
                <button
                  onClick={handleCompleteConfirm}
                  disabled={completing}
                  className={`flex-1 px-4 py-2 rounded-xl bg-green-600 text-white font-medium transition-colors shadow-lg shadow-green-200 dark:shadow-none ${
                    completing
                      ? "opacity-50 cursor-not-allowed hover:bg-green-600"
                      : "hover:bg-green-700"
                  }`}
                >
                  {completing ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      {t("bookings.completing")}
                    </>
                  ) : (
                    t("bookings.confirmComplete")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingList;
